// SCR-STAFF-207 — تخفیف‌ها و کوپن‌ها (US-CAT-060 تا 063؛ AP-CAT-03): قواعد خودکار، کوپن و انتخاب دستی با نوع مقدار، دامنه، شرایط پایه
// (AND)، ترکیب‌پذیری و اولویت، سقف‌ها و بودجه؛ آمار مصرف (Commit و رزرو) و باقیمانده بودجه؛ فعال‌سازی و توقف.
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type DiscountCondition,
  type DiscountRuleRequest,
  type DiscountRuleView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { catKeys, fromBasisPoints, toBasisPoints, useActiveServices, useCategories } from './common';

export const SCR_STAFF_207 = 'SCR-STAFF-207';

const ACTIVATIONS = ['Automatic', 'Coupon', 'Manual'] as const;
const VALUE_TYPES = ['Percent', 'FixedAmount', 'FixedPrice', 'FreeUnits'] as const;
const SCOPES = ['All', 'Category', 'Service'] as const;
const COMBINABILITY = ['Combinable', 'Exclusive'] as const;
const GENDERS = ['', 'Female', 'Male', 'Other'] as const;

const STATUS_TONE: Record<string, StatusTone> = {
  Draft: 'pending',
  Scheduled: 'pending',
  Active: 'active',
  Paused: 'inactive',
  Exhausted: 'cancelled',
  Expired: 'inactive',
};

const optionalInt = z
  .string()
  .refine((value) => value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0), {
    message: 'errors:validation.invalid',
  });

const ruleSchema = z
  .object({
    code: z.string().trim().min(1, 'common:validation.required').max(32),
    name: z.string().trim().min(1, 'common:validation.required').max(200),
    activation: z.enum(ACTIVATIONS),
    couponCode: z.string(),
    valueType: z.enum(VALUE_TYPES),
    percent: z.string(),
    amount: z.number().int().nullable().optional(),
    freeEvery: z.string(),
    scope: z.enum(SCOPES),
    scopeIds: z.array(z.string()),
    combinability: z.enum(COMBINABILITY),
    priority: z.string(),
    validFrom: z.string().nullable().optional(),
    validTo: z.string().nullable().optional(),
    budget: z.number().int().nullable().optional(),
    maxPerInvoice: z.number().int().nullable().optional(),
    maxPerPatient: optionalInt,
    maxTotal: optionalInt,
    firstVisit: z.boolean(),
    birthMonth: z.boolean(),
    gender: z.string(),
    minAge: optionalInt,
    maxAge: optionalInt,
    minAmount: z.number().int().nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.valueType === 'Percent' && !(Number(value.percent) > 0 && Number(value.percent) <= 100)) {
      ctx.addIssue({ code: 'custom', path: ['percent'], message: 'errors:validation.invalid' });
    }
    if (
      (value.valueType === 'FixedAmount' || value.valueType === 'FixedPrice') &&
      !(Number(value.amount) >= 0 && value.amount !== null && value.amount !== undefined)
    ) {
      ctx.addIssue({ code: 'custom', path: ['amount'], message: 'common:validation.required' });
    }
    if (
      value.valueType === 'FreeUnits' &&
      !(Number(value.freeEvery) >= 2 && Number(value.freeEvery) <= 100)
    ) {
      ctx.addIssue({ code: 'custom', path: ['freeEvery'], message: 'errors:validation.invalid' });
    }
    if (value.activation === 'Coupon' && !/^[A-Za-z0-9]{4,20}$/.test(value.couponCode.trim())) {
      ctx.addIssue({ code: 'custom', path: ['couponCode'], message: 'errors:validation.invalid' });
    }
    if (value.scope !== 'All' && value.scopeIds.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['scopeIds'], message: 'common:validation.required' });
    }
  });
type RuleForm = z.input<typeof ruleSchema>;

/** تاریخ کسب‌وکاری ← آغاز روز به وقت تهران (ADR-014). */
const startOfDay = (date: string) => `${date}T00:00:00+03:30`;
const nextDay = (date: string) => {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return startOfDay(value.toISOString().slice(0, 10));
};

function conditionsOf(form: RuleForm): DiscountCondition[] {
  const conditions: DiscountCondition[] = [];
  if (form.firstVisit) conditions.push({ kind: 'FirstVisit' });
  if (form.birthMonth) conditions.push({ kind: 'BirthMonth' });
  if (form.gender) conditions.push({ kind: 'Gender', values: [form.gender] });
  if (form.minAge !== '' || form.maxAge !== '') {
    conditions.push({
      kind: 'AgeRange',
      min: form.minAge === '' ? null : Number(form.minAge),
      max: form.maxAge === '' ? null : Number(form.maxAge),
    });
  }
  if (form.minAmount) conditions.push({ kind: 'MinAmount', amount: form.minAmount });
  return conditions;
}

function toRequest(form: RuleForm, rowVersion?: number | string): DiscountRuleRequest {
  const value =
    form.valueType === 'Percent'
      ? toBasisPoints(Number(form.percent))
      : form.valueType === 'FreeUnits'
        ? Number(form.freeEvery)
        : Number(form.amount);
  const rials = (amount: number | null | undefined) => (amount ? { amount, currency: 'IRR' } : null);
  return {
    code: form.code.trim(),
    name: form.name.trim(),
    description: null,
    activation: form.activation,
    valueType: form.valueType,
    value,
    scope: form.scope,
    scopeIds: form.scope === 'All' ? [] : form.scopeIds,
    combinability: form.combinability,
    priority: Number(form.priority || 100),
    validFrom: form.validFrom ? startOfDay(form.validFrom) : null,
    validTo: form.validTo ? nextDay(form.validTo) : null,
    maxAmountPerInvoice: rials(form.maxPerInvoice),
    maxUsesPerPatient: form.maxPerPatient === '' ? null : Number(form.maxPerPatient),
    maxUsesTotal: form.maxTotal === '' ? null : Number(form.maxTotal),
    budget: rials(form.budget),
    conditions: conditionsOf(form),
    couponCode: form.activation === 'Coupon' ? form.couponCode.trim().toUpperCase() : null,
    rowVersion: rowVersion === undefined ? null : Number(rowVersion),
  } as DiscountRuleRequest;
}

function formOf(rule: DiscountRuleView | null): RuleForm {
  const condition = (kind: string) => rule?.conditions.find((item) => item.kind === kind);
  const age = condition('AgeRange');
  return {
    code: rule?.code ?? '',
    name: rule?.name ?? '',
    activation: rule?.activation ?? 'Automatic',
    couponCode: rule?.couponCode ?? '',
    valueType: rule?.valueType ?? 'Percent',
    percent: rule?.valueType === 'Percent' ? String(fromBasisPoints(rule.value) ?? '') : '10',
    amount:
      rule && (rule.valueType === 'FixedAmount' || rule.valueType === 'FixedPrice')
        ? Number(rule.value)
        : null,
    freeEvery: rule?.valueType === 'FreeUnits' ? String(rule.value) : '5',
    scope: (rule?.scope as RuleForm['scope'] | undefined) ?? 'All',
    scopeIds: [...(rule?.scopeIds ?? [])],
    combinability: rule?.combinability ?? 'Combinable',
    priority: String(rule?.priority ?? 100),
    validFrom: rule?.validFrom ? rule.validFrom.slice(0, 10) : null,
    validTo: null,
    budget: rule?.budget ? Number(rule.budget.amount) : null,
    maxPerInvoice: rule?.maxAmountPerInvoice ? Number(rule.maxAmountPerInvoice.amount) : null,
    maxPerPatient: rule?.maxUsesPerPatient ? String(rule.maxUsesPerPatient) : '',
    maxTotal: rule?.maxUsesTotal ? String(rule.maxUsesTotal) : '',
    firstVisit: Boolean(condition('FirstVisit')),
    birthMonth: Boolean(condition('BirthMonth')),
    gender: condition('Gender')?.values?.[0] ?? '',
    minAge: age?.min !== undefined && age?.min !== null ? String(age.min) : '',
    maxAge: age?.max !== undefined && age?.max !== null ? String(age.max) : '',
    minAmount: condition('MinAmount')?.amount ? Number(condition('MinAmount')?.amount) : null,
  };
}

function RuleDialog({ rule, onClose }: { rule: DiscountRuleView | null; onClose: () => void }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const services = useActiveServices();
  const categories = useCategories();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<RuleForm>({
    resolver: zodResolver(ruleSchema),
    defaultValues: formOf(rule),
  });
  const [activation, valueType, scope] = useWatch({ control, name: ['activation', 'valueType', 'scope'] });
  const scopeOptions =
    scope === 'Category'
      ? (categories.data?.items ?? []).map((item) => ({ id: item.id, name: item.name }))
      : services.options.map((item) => ({ id: item.id, name: item.name }));

  const save = useMutation({
    mutationFn: (form: RuleForm) =>
      unwrap(
        rule
          ? tenantApi.PUT('/api/v1/catalog/discount-rules/{id}', {
              params: { path: { id: rule.id } },
              body: toRequest(form, rule.rowVersion),
            })
          : tenantApi.POST('/api/v1/catalog/discount-rules', {
              body: toRequest(form),
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'couponCode'],
        codeFields: { 'PHX-CAT-0901': 'code', 'PHX-CAT-0904': 'couponCode' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      maxWidth="md"
      screenId="SCR-STAFF-207-RULE"
      titleKey={rule ? 'cat:discounts.edit_title' : 'cat:discounts.create_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="cat:discounts.form.code"
          required
          disabled={Boolean(rule)}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'discount-code' } }}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="cat:discounts.form.name"
          required
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'discount-name' } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="activation"
          labelKey="cat:discounts.form.activation"
          options={ACTIVATIONS.map((value) => ({ value, labelKey: `cat:discounts.activation.${value}` }))}
        />
        {activation === 'Coupon' && (
          <TextFieldRHF
            control={control}
            name="couponCode"
            labelKey="cat:discounts.form.coupon_code"
            required
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'discount-coupon' } }}
          />
        )}
        <SelectRHF
          control={control}
          name="combinability"
          labelKey="cat:discounts.form.combinability"
          options={COMBINABILITY.map((value) => ({
            value,
            labelKey: `cat:discounts.combinability.${value}`,
          }))}
        />
        <TextFieldRHF
          control={control}
          name="priority"
          labelKey="cat:discounts.form.priority"
          type="number"
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="valueType"
          labelKey="cat:discounts.form.value_type"
          options={VALUE_TYPES.map((value) => ({ value, labelKey: `cat:discounts.value_type.${value}` }))}
        />
        {valueType === 'Percent' && (
          <TextFieldRHF
            control={control}
            name="percent"
            labelKey="cat:discounts.form.percent"
            type="number"
            required
            slotProps={{ htmlInput: { min: 0.01, max: 100, step: 0.01, 'data-testid': 'discount-percent' } }}
          />
        )}
        {(valueType === 'FixedAmount' || valueType === 'FixedPrice') && (
          <MoneyFieldRHF control={control} name="amount" labelKey="cat:discounts.form.amount" required />
        )}
        {valueType === 'FreeUnits' && (
          <TextFieldRHF
            control={control}
            name="freeEvery"
            labelKey="cat:discounts.form.free_every"
            type="number"
            required
          />
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="scope"
          labelKey="cat:discounts.form.scope"
          options={SCOPES.map((value) => ({ value, labelKey: `cat:discounts.scope.${value}` }))}
        />
        {scope !== 'All' && (
          <Controller
            control={control}
            name="scopeIds"
            render={({ field, fieldState }) => (
              <Autocomplete
                multiple
                fullWidth
                options={scopeOptions.map((option) => option.id)}
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(id) =>
                  scopeOptions.find((option) => option.id === id)?.name ?? services.name(id)
                }
                filterOptions={(options, state) =>
                  scope === 'Service'
                    ? options
                    : options.filter((id) =>
                        (scopeOptions.find((option) => option.id === id)?.name ?? '').includes(
                          state.inputValue,
                        ),
                      )
                }
                onInputChange={(_, value, reason) => {
                  if (reason === 'input' && scope === 'Service') {
                    services.search(value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('cat:discounts.form.scope_ids')}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                )}
              />
            )}
          />
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={control} name="validFrom" labelKey="cat:discounts.form.valid_from" />
        <JalaliDatePickerRHF control={control} name="validTo" labelKey="cat:discounts.form.valid_to" />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <MoneyFieldRHF control={control} name="budget" labelKey="cat:discounts.form.budget" />
        <MoneyFieldRHF control={control} name="maxPerInvoice" labelKey="cat:discounts.form.max_per_invoice" />
        <TextFieldRHF
          control={control}
          name="maxPerPatient"
          labelKey="cat:discounts.form.max_per_patient"
          type="number"
          slotProps={{ htmlInput: { min: 1, 'data-testid': 'discount-max-patient' } }}
        />
        <TextFieldRHF
          control={control}
          name="maxTotal"
          labelKey="cat:discounts.form.max_total"
          type="number"
        />
      </Stack>
      <Typography variant="subtitle2">{t('cat:discounts.form.conditions')}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <CheckboxRHF control={control} name="firstVisit" labelKey="cat:discounts.form.first_visit" />
        <CheckboxRHF control={control} name="birthMonth" labelKey="cat:discounts.form.birth_month" />
        <SelectRHF
          control={control}
          name="gender"
          labelKey="cat:discounts.form.gender"
          options={GENDERS.map((value) => ({
            value,
            labelKey: value ? `cat:discounts.gender.${value}` : 'cat:discounts.form.any',
          }))}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="minAge" labelKey="cat:discounts.form.min_age" type="number" />
        <TextFieldRHF control={control} name="maxAge" labelKey="cat:discounts.form.max_age" type="number" />
        <MoneyFieldRHF control={control} name="minAmount" labelKey="cat:discounts.form.min_amount" />
      </Stack>
    </FormDialog>
  );
}

export function DiscountsPage() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.discount.manage');
  const [dialog, setDialog] = useState<{ rule: DiscountRuleView | null } | null>(null);
  const list = useQuery({
    queryKey: catKeys.discounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/discount-rules')),
  });
  const action = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'activate' | 'pause' }) =>
      unwrap(
        kind === 'activate'
          ? tenantApi.POST('/api/v1/catalog/discount-rules/{id}:activate', { params: { path: { id } } })
          : tenantApi.POST('/api/v1/catalog/discount-rules/{id}:pause', { params: { path: { id } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) }),
  });

  const valueOf = (row: DiscountRuleView) => {
    switch (row.valueType) {
      case 'Percent':
        return t('cat:discounts.value_percent', { value: faDigits(fromBasisPoints(row.value) ?? 0) });
      case 'FreeUnits':
        return t('cat:discounts.value_free', { value: faDigits(row.value) });
      default:
        return <Money amountMinor={row.value} />;
    }
  };

  const columns: DataTableColumn<DiscountRuleView>[] = [
    { id: 'code', headerKey: 'cat:discounts.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'name',
      headerKey: 'cat:discounts.columns.name',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{row.name}</span>
          {row.couponCode && (
            <Typography variant="caption" color="text.secondary">
              <bdi dir="ltr">{row.couponCode}</bdi>
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: 'activation',
      headerKey: 'cat:discounts.columns.activation',
      cell: (row) =>
        `${t(`cat:discounts.activation.${row.activation}`)} · ${t(`cat:discounts.combinability.${row.combinability}`)}`,
    },
    { id: 'value', headerKey: 'cat:discounts.columns.value', cell: valueOf },
    {
      id: 'status',
      headerKey: 'cat:discounts.columns.status',
      cell: (row) => (
        <span data-testid={`discount-status-${row.code}`}>
          <StatusChip
            tone={STATUS_TONE[row.status] ?? 'pending'}
            labelKey={`cat:discounts.status.${row.status}`}
          />
        </span>
      ),
    },
    {
      id: 'usage',
      headerKey: 'cat:discounts.columns.usage',
      cell: (row) => (
        <span>
          {t('cat:discounts.usage_value', { count: faDigits(row.usage.usesCommitted) })}
          <Money amountMinor={row.usage.amountCommitted.amount} />
        </span>
      ),
    },
    {
      id: 'budget',
      headerKey: 'cat:discounts.columns.budget',
      cell: (row) =>
        row.usage.budgetRemaining ? (
          <Money amountMinor={row.usage.budgetRemaining.amount} />
        ) : (
          t('cat:discounts.unlimited')
        ),
    },
    {
      id: 'actions',
      headerKey: 'cat:discounts.columns.actions',
      cell: (row) =>
        write.canWrite ? (
          <Stack direction="row" spacing={1}>
            {(row.status === 'Draft' || row.status === 'Paused' || row.status === 'Exhausted') && (
              <Button size="small" onClick={() => setDialog({ rule: row })}>
                {t('cat:discounts.edit')}
              </Button>
            )}
            {(row.status === 'Draft' || row.status === 'Paused' || row.status === 'Exhausted') && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => action.mutate({ id: row.id, kind: 'activate' })}
                data-testid={`discount-activate-${row.code}`}
              >
                {t('cat:discounts.activate')}
              </Button>
            )}
            {(row.status === 'Active' || row.status === 'Scheduled') && (
              <Button
                size="small"
                color="warning"
                onClick={() => action.mutate({ id: row.id, kind: 'pause' })}
              >
                {t('cat:discounts.pause')}
              </Button>
            )}
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_207}
      titleKey="cat:discounts.title"
      subtitleKey="cat:discounts.subtitle"
      filters={<WriteNotice write={write} />}
    >
      <Stack spacing={2}>
        {list.data && (
          <Alert severity="info" data-testid="discount-cap">
            {t('cat:discounts.cap_notice', {
              percent: faDigits(fromBasisPoints(list.data.capBasisPoints) ?? 50),
            })}
          </Alert>
        )}
        {action.error ? <ErrorState error={action.error} compact /> : null}
        {write.canWrite && (
          <Stack direction="row">
            <Button
              variant="contained"
              onClick={() => setDialog({ rule: null })}
              data-testid="discount-create"
            >
              {t('cat:discounts.create')}
            </Button>
          </Stack>
        )}
        <DataTable
          captionKey="cat:discounts.caption"
          columns={columns}
          rows={list.data?.items}
          getRowId={(row) => row.id}
          isLoading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          emptyTitleKey="cat:discounts.empty"
        />
      </Stack>
      {dialog && <RuleDialog rule={dialog.rule} onClose={() => setDialog(null)} />}
    </ListPage>
  );
}
